/**
 * Admin Build Inquiries — kanban board for leads submitted through
 * /build.html. Columns: New → Contacted → Quoted → Won → Lost.
 *
 * Admin-only (gated server-side; the route in App.js also restricts via
 * allowedRoles=['admin']). Each card shows the lead, drag-free status
 * transitions via a status dropdown, an inline notes editor, and a
 * danger-zone delete for spam.
 *
 * Strictly additive — does not touch any existing page or component.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '../../components/ui/select';
import {
  Inbox, MessageCircle, FileText, Trophy, XCircle,
  Trash2, Save, Loader2, Mail, Phone, Sparkles,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { formatDateTime } from '../../lib/utils';

const COLUMN_META = {
  new:       { label: 'New',       icon: Inbox,         accent: 'border-l-[#B89B5E] text-[#8E7846]', dot: 'bg-[#B89B5E]' },
  contacted: { label: 'Contacted', icon: MessageCircle, accent: 'border-l-blue-500 text-blue-700',   dot: 'bg-blue-500' },
  quoted:    { label: 'Quoted',    icon: FileText,      accent: 'border-l-amber-500 text-amber-700', dot: 'bg-amber-500' },
  won:       { label: 'Won',       icon: Trophy,        accent: 'border-l-emerald-600 text-emerald-700', dot: 'bg-emerald-600' },
  lost:      { label: 'Lost',      icon: XCircle,       accent: 'border-l-stone-400 text-stone-500', dot: 'bg-stone-400' },
};

export default function BuildInquiries() {
  const { get, patch, del } = useApi();
  const [data, setData] = useState({ columns: [], by_status: {}, total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get('/admin/build-inquiries');
      setData(r || { columns: [], by_status: {}, total: 0 });
    } catch (e) {
      toast.error('Could not load inquiries');
    } finally {
      setLoading(false);
    }
  }, [get]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function changeStatus(id, status) {
    try {
      await patch(`/admin/build-inquiries/${id}/status`, { status });
      toast.success(`Moved to ${COLUMN_META[status]?.label || status}`);
      fetchAll();
    } catch (e) {
      toast.error('Status update failed');
    }
  }

  async function saveNotes(id, notes) {
    try {
      await patch(`/admin/build-inquiries/${id}/notes`, { notes });
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Could not save notes');
    }
  }

  async function deleteInquiry(id) {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;
    try {
      await del(`/admin/build-inquiries/${id}`);
      toast.success('Inquiry deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="build-inquiries-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#B89B5E]" />
              <h1 className="font-serif text-3xl">Build Inquiries</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Leads from <code>build.html</code> · {data.total || 0} total ·
              flow them through New → Contacted → Quoted → Won/Lost.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {(data.columns || []).map((col) => (
              <KanbanColumn
                key={col}
                column={col}
                inquiries={data.by_status?.[col] || []}
                onChangeStatus={changeStatus}
                onSaveNotes={saveNotes}
                onDelete={deleteInquiry}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function KanbanColumn({ column, inquiries, onChangeStatus, onSaveNotes, onDelete }) {
  const meta = COLUMN_META[column] || COLUMN_META.new;
  const Icon = meta.icon;
  return (
    <div className="flex flex-col gap-3" data-testid={`column-${column}`}>
      <div className={`flex items-center justify-between px-3 py-2 rounded-sm border bg-white ${meta.accent} border-l-4`}>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" />
          <span className="font-medium text-sm">{meta.label}</span>
        </div>
        <Badge variant="outline" className="rounded-sm text-xs" data-testid={`count-${column}`}>
          {inquiries.length}
        </Badge>
      </div>
      <div className="space-y-3 min-h-[60px]">
        {inquiries.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3 italic">
            No inquiries here.
          </p>
        ) : (
          inquiries.map((i) => (
            <InquiryCard
              key={i.id}
              inquiry={i}
              onChangeStatus={onChangeStatus}
              onSaveNotes={onSaveNotes}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
}

function InquiryCard({ inquiry, onChangeStatus, onSaveNotes, onDelete }) {
  const [notes, setNotes] = useState(inquiry.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const dirty = useMemo(() => notes !== (inquiry.notes || ''), [notes, inquiry.notes]);
  const contactIsEmail = /\S+@\S+\.\S+/.test(inquiry.contact || '');
  const ContactIcon = contactIsEmail ? Mail : Phone;
  const contactHref = contactIsEmail
    ? `mailto:${inquiry.contact}`
    : `tel:${(inquiry.contact || '').replace(/[^+0-9]/g, '')}`;

  return (
    <Card className="rounded-sm border" data-testid={`inquiry-card-${inquiry.id}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
          <span className="truncate">{inquiry.name}</span>
          <button
            onClick={() => onDelete(inquiry.id)}
            className="text-stone-400 hover:text-red-600 shrink-0"
            aria-label="Delete inquiry"
            data-testid={`delete-${inquiry.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </CardTitle>
        <a
          href={contactHref}
          className="text-xs text-muted-foreground flex items-center gap-1.5 hover:text-[#B89B5E] truncate"
        >
          <ContactIcon className="h-3 w-3 shrink-0" />
          {inquiry.contact}
        </a>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            What they need
          </p>
          <p className="text-xs text-stone-700 whitespace-pre-wrap line-clamp-4">
            {inquiry.what_needed}
          </p>
        </div>
        {inquiry.problem && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
              Problem
            </p>
            <p className="text-xs text-stone-700 whitespace-pre-wrap line-clamp-3">
              {inquiry.problem}
            </p>
          </div>
        )}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            Notes
          </p>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Follow-up notes…"
            className="text-xs"
            data-testid={`notes-${inquiry.id}`}
          />
          {dirty && (
            <Button
              size="sm"
              variant="outline"
              className="mt-1.5 h-7 text-xs"
              disabled={savingNotes}
              onClick={async () => {
                setSavingNotes(true);
                try { await onSaveNotes(inquiry.id, notes); }
                finally { setSavingNotes(false); }
              }}
              data-testid={`save-notes-${inquiry.id}`}
            >
              {savingNotes ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save notes
            </Button>
          )}
        </div>
        <Select
          value={inquiry.status}
          onValueChange={(v) => onChangeStatus(inquiry.id, v)}
        >
          <SelectTrigger className="h-8 text-xs" data-testid={`status-trigger-${inquiry.id}`}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(COLUMN_META).map(([key, m]) => (
              <SelectItem key={key} value={key}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground">
          Submitted {formatDateTime(inquiry.created_at)}
        </p>
      </CardContent>
    </Card>
  );
}
